import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ShieldCheck, Check, Play, ArrowRight } from "lucide-react";
import { fetchPages, fetchBehandlinger, fetchPriser } from "../lib/cms";
import { videoer, virksomhedsinfo } from "../data";
import VideoModal from "../components/VideoModal";

export default function Behandlinger() {
  const [pageData, setPageData] = useState(null);
  const [behandlinger, setBehandlinger] = useState([]);
  const [priser, setPriser] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    async function loadData() {
      const pages = await fetchPages();
      const bPage = pages.find((p) => p.page_slug === "behandlinger");
      setPageData(bPage);

      const list = await fetchBehandlinger();
      setBehandlinger(list);

      const priserList = await fetchPriser();
      setPriser(priserList);
      setLoading(false);
    }
    loadData();
  }, []);

  // Sæt sidetitel og meta-beskrivelse
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || "Behandlinger & Priser - RG Fodpleje";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "Se vores udvalg af professionelle fodbehandlinger.");
      }
    }
  }, [pageData]);

  return (
    <div className="bg-[#FAF8F5] text-[#2D2A26] min-h-screen py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Overskrift sektion */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-semibold tracking-widest uppercase text-[#4A5D4E] bg-[#E8EFEA] px-3 py-1.5 rounded-full">
            Prisliste & Info
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2D2A26]">
            Prisliste
          </h1>
          {pageData && (
            <div 
              className="text-[#6B6661] font-light leading-relaxed max-w-2xl mx-auto"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
            />
          )}
        </div>

        {/* Prisliste hurtig oversigt */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-[#EAE5DF] shadow-sm p-6 sm:p-8 mb-20 space-y-4">
          <h2 className="font-serif text-xl font-semibold text-[#2D2A26] border-b border-[#EAE5DF] pb-3">
            Hurtig prisoversigt
          </h2>
          <div className="divide-y divide-[#FAF8F5]">
            {priser.map((p) => (
              <div key={p.id || p.titel} className="py-4 flex justify-between items-center gap-4">
                <span className="font-medium text-[#2D2A26]">{p.titel}</span>
                <span className="font-semibold text-[#4A5D4E] whitespace-nowrap">{p.pris},00 DKK</span>
              </div>
            ))}
          </div>
          <div className="pt-4 text-center">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-sm"
            >
              <Calendar className="h-4 w-4" />
              <span>Book tid online</span>
            </a>
          </div>
        </div>

        {/* Detaljerede behandlinger */}
        <div className="space-y-20 mb-20">
          <h2 className="font-serif text-3xl font-semibold text-[#2D2A26] text-center border-b border-[#EAE5DF] pb-6">
            Uddybende beskrivelser
          </h2>
          
          {behandlinger.map((b, index) => (
            <div
              key={b.treatment_slug}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Billede */}
              <div className={`lg:col-span-5 ${index % 2 === 1 ? "lg:order-last" : ""}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-md border-4 border-white bg-stone-100 aspect-[4/3]">
                  <img
                    src={b.image && b.image[0]}
                    alt={b.title}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute bottom-4 right-4 bg-[#4A5D4E] text-[#FAF8F5] font-semibold px-4 py-2 rounded-full text-sm shadow-md">
                    {(() => {
                      const prisMatch = priser.find(p => p.titel.toLowerCase() === b.title.toLowerCase());
                      return prisMatch ? prisMatch.pris : b.price;
                    })()},00 DKK
                  </div>
                </div>
              </div>

              {/* Tekst */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A5D4E] uppercase tracking-wider">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Professionel Fodpleje</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D2A26]">
                  {b.title}
                </h3>
                
                {/* Beskrivelse (bevarer stavefejl verbatim) */}
                <div
                  className="text-[#6B6661] font-light leading-relaxed space-y-4 prose prose-stone max-w-none"
                  dangerouslySetInnerHTML={{ __html: b.description }}
                />

                <div className="pt-4 flex flex-wrap items-center gap-3">
                  <a
                    href="https://rg-fodpleje.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-sm"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>Book denne behandling</span>
                  </a>

                  {(b.treatment_slug === "behandling-af-ligtorne" || b.title?.toLowerCase().includes("ligtorn")) && (
                    <Link
                      to="/behandling-af-ligtorn"
                      className="inline-flex items-center gap-2 bg-white hover:bg-[#F3EFEA] text-[#4A5D4E] border border-[#4A5D4E] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-sm"
                    >
                      <span>Læs mere</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Sektion: Behandling i praksis */}
        <section className="bg-[#F3EFEA] rounded-3xl p-8 sm:p-12 border border-[#EAE5DF] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-semibold tracking-widest uppercase text-[#4A5D4E]">
              Klinikvideo
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D2A26]">
              Se hvordan en professionel behandling foregår
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              I klinikken anvendes kun det bedste udstyr og godkendte metoder. Alt værktøj steriliseres i en autoklave efter brug, så du altid er sikret en hygiejnisk og tryg oplevelse. Se en kort video af klinikken og behandlingerne her.
            </p>
            <div className="flex items-center gap-4 text-sm text-[#4A5D4E] font-medium">
              <Check className="h-5 w-5 shrink-0" />
              <span>SADF-godkendt klinik</span>
            </div>
          </div>
          
          <div className="lg:col-span-6">
            <div 
              onClick={() => setIsModalOpen(true)}
              className="relative rounded-2xl overflow-hidden shadow-lg border-4 border-white bg-stone-100 aspect-video cursor-pointer group"
            >
              <video
                id="treatment-video"
                src={videoer[2].url} // RG Fodpleje video 3 justeret.mp4
                poster="https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/6ff38804-473d-4647-91a8-2151bb31a68d.JPEG" // IMG_0551.JPEG (Rikke i døren)
                className="h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <div
                  className="h-16 w-16 rounded-full bg-[#FAF8F5] group-hover:bg-white text-[#4A5D4E] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
                >
                  <Play className="h-6 w-6 fill-current ml-1" />
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={videoer[2].url}
        title="Behandling i praksis - RG Fodpleje"
      />
    </div>
  );
}
